import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import moment from "moment";
import PropTypes from "prop-types";

import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import CommentDisplayModal from "components/CommentDisplayModal";
import Table from "components/Table";
import TextButton from "components/TextButton";
import { fetchPicklistOptions } from "store/actions/picklists";
import { picklistsSelectors } from "store/selectors";

import { compareBy, getDatesComparator, numbersComparator, stringsComparator } from "../../utils/sort";
import SortingControls from "../Table/SortingControls";
import { ReviewRow } from "./components";
import styles from "./styles.css";

const RoutingTable = ({
	formRules,
	reviewEntries,
	certificationEntries,
	currentRound,
	hasError,
	isLoading,
	dictionary
}) => {
	const [ commentTitle, setCommentTitle ] = useState("");
	const [ comment, setComment ] = useState("");
	const [ isCommentModalOpen, setIsCommentModalOpen ] = useState(false);
	const [ sortDirection, setSortDirection ] = useState({ columnId: "rule", direction: "asc" });
	const [ sortedEntries, setSortedEntries ] = useState(null);

	const { options: roles } = useSelector(picklistsSelectors.researchTeamRoles);

	const entries = useMemo(() => {
		const flatRules = [];
		formRules?.forEach(item => {
			flatRules.push.apply(flatRules, item);
		});


		flatRules.sort(function (a, b) {
			return (a === null) - (b === null) ||
				+(moment(a.date).isAfter(moment(b.date))) ||
				-(moment(a.date).isBefore(moment(b.date)));
		});

		// Submission round or Max round number in arr
		// (-1 for old submissions, 0 if we were able to infer current round data)
		const resolvedCurrentRound = currentRound ?? [ ...reviewEntries, ...certificationEntries ]
			.reduce((max, entry) => Math.max(max, entry.round ?? -1), -1);

		const getRuleByEntry = entry => {
			const ruleId = entry.ruleId || entry.reviewTarget?.ruleId;
			if (!ruleId) {return null;}

			return flatRules.find(rule => rule.ruleId === ruleId);
		};

		const getRoutingEventOrder = entry => {
			if (resolvedCurrentRound === -1) {return null;}
			if ((entry.round ?? -1) !== resolvedCurrentRound) {return null;}
			const rule = getRuleByEntry(entry);

			return rule?.orderIndex;
		};

		const entryMapper = entryType => {
			return entry => ({
				...entry, entryType,
				orderIndex: getRoutingEventOrder(entry),
				ruleId: entry.ruleId || entry.reviewTarget?.ruleId,
				targetName: entry.reviewTarget?.targetName,
				targetIndex: entry.reviewTarget?.targetIndex,
				rollupTargetIndex: entry.reviewTarget?.rollupTargetIndex,
				date: entry.date || undefined
			});
		};

		return [
			...reviewEntries.map(entryMapper("review")),
			...certificationEntries.map(entryMapper("certification"))
		];
	}, [ formRules, reviewEntries, certificationEntries ]);


	const compareByDate = (a, b, direction) => {
		return compareBy(a, b, [
			{ getter: obj => obj.date, comparator: getDatesComparator(a, b, direction) }
		]);
	};

	const compareByRule = (a, b, direction) => {
		const compResult = compareBy(a, b, [
			{ getter: obj => obj.orderIndex, comparator: numbersComparator },
			{ getter: obj => obj.ruleId, comparator: stringsComparator },
			{ getter: obj => obj.targetIndex, comparator: numbersComparator },
			{ getter: obj => obj.rollupTargetIndex, comparator: numbersComparator },
			{ getter: obj => obj.targetName, comparator: stringsComparator }
		]);

		return direction === "asc" ? compResult : -compResult;
	};

	useEffect(() => {
		setSortedEntries(() => {
			return [ ...(entries ?? []) ].sort((a, b) => {
				switch (sortDirection.columnId) {
				case "date":
					return compareByDate(a, b, sortDirection.direction);
				case "rule":
					return compareByRule(a, b, sortDirection.direction);
				default:
					return 0;
				}
			});
		});
	}, [ sortDirection, entries ]);

	const enableOrderColumn = entries.some(entry => entry.orderIndex !== null);

	const reducedRoles = useMemo(() => {
		return roles.reduce((map, obj) => {
			map[obj.id] = obj.text;

			return map;
		}, {});
	}, [ roles ]);

	useEffect(() => {
		fetchPicklistOptions("researchTeamRoles");
	},[]);

	const onCommentOpen = useCallback((createdBy, comment) => {
		const title = isNonEmptyString(createdBy)
			? `${dictionary.commentModal?.titleWithCreator || "Routing Comment from"} ${createdBy}`
			: dictionary.commentModal?.titleWithoutCreator || "Routing Comment";
		setCommentTitle(title);
		setComment(comment);
		setIsCommentModalOpen(true);
	});

	const onCommentClose = () => {
		setCommentTitle("");
		setComment("");
		setIsCommentModalOpen(false);
	};

	const renderCertificationRow = useCallback((entry, index) => {
		return (
			<Table.Row key={`routing-certification-entry-${index}`}>
				{enableOrderColumn && <Table.Cell>
					{entry.orderIndex ?? "--"}
				</Table.Cell>}
				<Table.Cell>
					{reducedRoles[entry.roleId]}
				</Table.Cell>
				<Table.Cell>
					{entry.fullName}
				</Table.Cell>
				<Table.Cell className={styles.decision}>
					{dictionary.certification[entry.status] ?
						dictionary.certification[entry.status]
						:
						<i>{dictionary.certification.emptyStatus || "Not Yet Active"}</i>
					}
				</Table.Cell>
				<Table.Cell>
					{isNonEmptyString(entry.date) && (
						<FormattedDatetime date={entry.date} />
					)}
				</Table.Cell>
				<Table.Cell>
					{ isNonEmptyString(entry.comment) &&
					<TextButton appearAsLink onClick={() => onCommentOpen(entry.createdBy, entry.comment)}>
						{dictionary.viewComment || "view"}
					</TextButton>
					}
				</Table.Cell>
			</Table.Row>
		);
	}, [ dictionary.certification, dictionary.viewComment, enableOrderColumn, onCommentOpen, reducedRoles ]);

	const sortColumn = columnId => {
		setSortDirection(prevState => {
			if (prevState.columnId === columnId) {
				return {
					...prevState,
					direction: prevState.direction === "asc" ? "desc" : "asc"
				};
			} else {
				return { columnId: columnId, direction: "asc" };
			}
		});
	};

	const error = hasError
		? dictionary.errors?.unableToLoad || "Unable to load routing information"
		: false;

	const renderTableBody = useCallback(() => {
		if (isNonEmptyString(error)) {
			return <Table.ErrorRow>{error}</Table.ErrorRow>;
		}

		if (isNonEmptyArray(sortedEntries)) {
			return sortedEntries.map((entry, k) => {
				switch (entry.entryType) {
				case "review":
					return (
						<ReviewRow
							enableOrderColumn={enableOrderColumn}
							key={`routing-review-entry-${k}`}
							entry={entry}
							onCommentOpen={onCommentOpen}
							dictionary={dictionary}
						/>
					);
				case "certification":
					return renderCertificationRow(entry, k);
				}
			});
		}

		return (
			<Table.Row>
				<Table.Cell colSpan="5" className={styles.infoCell}>
					{dictionary.errors.noRouting}
				</Table.Cell>
			</Table.Row>
		);
	}, [ error, sortedEntries, dictionary, enableOrderColumn, onCommentOpen, renderCertificationRow ]);

	return (
		<div data-component="RoutingTable" className={styles.routingTable}>
			<Table isLoading={isLoading} error={error} captionText="Routing">
				<Table.Header>
					<Table.Row>
						{enableOrderColumn && <Table.HeaderCell className={styles.sortable}>
							<SortingControls
								columnId="rule"
								onClick={sortColumn}
								columnName={dictionary.columnHeaders?.reviewOrder || "Review Order"}
								sortedColumnId={sortDirection.columnId}
								sortDirection={sortDirection.direction}
							/>
						</Table.HeaderCell>}
						<Table.HeaderCell>
							{dictionary.columnHeaders?.team || "Team"}
						</Table.HeaderCell>
						<Table.HeaderCell>
							{dictionary.columnHeaders?.members || "Members"}
						</Table.HeaderCell>
						<Table.HeaderCell>
							{dictionary.columnHeaders?.status || "Status"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.sortable}>
							<SortingControls
								columnId="date"
								onClick={sortColumn}
								columnName={dictionary.columnHeaders?.date || "Date"}
								sortedColumnId={sortDirection.columnId}
								sortDirection={sortDirection.direction}
							/>
						</Table.HeaderCell>
						<Table.HeaderCell>
							{dictionary.columnHeaders?.comment || "Comment"}
						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>

				<Table.Body>
					{renderTableBody()}
				</Table.Body>
			</Table>

			<CommentDisplayModal
				title={commentTitle}
				comment={comment}
				open={isCommentModalOpen}
				onClose={onCommentClose}
			/>
		</div>
	);
};

RoutingTable.propTypes = {
	dictionary: PropTypes.shape({
		errors: PropTypes.shape({
		  noRouting: PropTypes.string
		})
	}),
	formRules: PropTypes.arrayOf(
		PropTypes.shape({
			ruleId: PropTypes.string,
			ruleType: PropTypes.string,
			formElementId: PropTypes.string,
			targetId: PropTypes.string,
			optionId: PropTypes.string,
			orderIndex: PropTypes.number
		})
	),
	reviewEntries: PropTypes.arrayOf(
		PropTypes.shape({
			reviewTarget: PropTypes.shape({
				targetId: PropTypes.string,
				targetName: PropTypes.string,
				taskType: PropTypes.string,
				targetIndex: PropTypes.number,
				rollupTargetIndex: PropTypes.number,
			}),
			reviewMembers: PropTypes.arrayOf(
				PropTypes.shape({
					taskId: PropTypes.string,
					name: PropTypes.string,
					hasReviewed: PropTypes.bool
				})
			),
			decision: PropTypes.string,
			date: PropTypes.string,
			comment: PropTypes.string
		})
	),
	hasError: PropTypes.bool,
	isLoading: PropTypes.bool
};

export default RoutingTable;
